import React, { useMemo } from 'react';

export default function ActiveSessionsModal({ onClose, onRefresh, loading, error, data, label }) {
    const sessions = useMemo(() => {
        if (!Array.isArray(data?.sessions)) return [];
        return [...data.sessions]
            .map((session) => ({
                ...session,
                durationMinutes: Number(session.durationMinutes ?? 0),
                start: session.start ? new Date(session.start).toISOString() : null,
                end: session.end ? new Date(session.end).toISOString() : null,
            }))
            .sort((a, b) => {
                const startA = a.start ? new Date(a.start).getTime() : 0;
                const startB = b.start ? new Date(b.start).getTime() : 0;
                return startA - startB;
            });
    }, [data]);

    const students = useMemo(() => {
        let base = [];
        if (Array.isArray(data?.students) && data.students.length > 0) {
            base = data.students;
        } else if (Array.isArray(data?.logins)) {
            base = data.logins.map((login) => ({
                id: login.id,
                name: login.name,
                email: login.email,
                totalMinutes: 0,
                sections: [],
                sessionIds: [],
                lastLoginAt: login.lastLoginAt || null,
            }));
        }

        return base
            .map((student) => ({
                ...student,
                sections: Array.isArray(student.sections) ? student.sections : [],
                sessionIds: Array.isArray(student.sessionIds) ? student.sessionIds : [],
                totalMinutes: Number(student.totalMinutes ?? 0),
                lastLoginAt: student.lastLoginAt ? new Date(student.lastLoginAt).toISOString() : null,
            }))
            .sort((a, b) => {
                const timeA = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
                const timeB = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
                return timeB - timeA;
            });
    }, [data]);

    const sectionTypes = useMemo(() => {
        const types = new Set();
        students.forEach((student) => {
            student.sections.forEach((section) => {
                if (section?.type) {
                    types.add(section.type);
                }
            });
        });
        return Array.from(types).sort();
    }, [students]);

    const studentsWithTrackedTime = useMemo(
        () => students.filter((student) => Number(student.totalMinutes || 0) > 0),
        [students]
    );

    const rangeStart = data?.range?.start ? new Date(data.range.start) : null;
    const lastFetched = data?.fetchedAt ? new Date(data.fetchedAt) : null;

    const formatSectionLabel = (type) => {
        if (!type) return 'Section';
        const normalized = String(type).toUpperCase();
        if (normalized === 'ROAD') return 'Road';
        if (normalized === 'SKILL') return 'Skill Yard';
        if (normalized === 'ONLINE') return 'On Platform';
        return normalized.charAt(0) + normalized.slice(1).toLowerCase();
    };

    const formatDuration = (minutes) => {
        const total = Math.max(0, Math.round(Number(minutes) || 0));
        const hours = Math.floor(total / 60);
        const remaining = total % 60;

        if (hours > 0 && remaining > 0) {
            return `${hours}h ${remaining}m`;
        }
        if (hours > 0) {
            return `${hours}h`;
        }
        return `${remaining}m`;
    };

    const formatStatus = (status) => {
        if (!status) return 'Unknown';
        return String(status)
            .toLowerCase()
            .split(/[_\s-]+/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    };

    const formatTimeRange = (startIso, endIso) => {
        if (!startIso || !endIso) return 'Time unavailable';
        const startDate = new Date(startIso);
        const endDate = new Date(endIso);

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
            return 'Time unavailable';
        }

        const sameDay = startDate.toDateString() === endDate.toDateString();
        const dateLabel = startDate.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
        const startTime = startDate.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
        });
        const endTime = endDate.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
        });

        if (sameDay) {
            return `${dateLabel} ${startTime} - ${endTime}`;
        }

        const endDateLabel = endDate.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });

        return `${dateLabel} ${startTime} - ${endDateLabel} ${endTime}`;
    };

    const formatLastLogin = (iso) => {
        if (!iso) return 'Not yet recorded';
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return 'Not yet recorded';
        return date.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getMinutesForSection = (student, type) => {
        const match = student.sections.find((section) => section.type === type);
        if (!match) return 0;
        return Number(match.minutes ?? 0);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 px-4 py-10">
            <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
                    <div>
                        <h2 className="text-2xl font-semibold text-slate-900">{label || 'Active Sessions'}</h2>
                        <p className="mt-1 text-sm text-slate-500">
                            {rangeStart
                                ? `Showing activity for ${rangeStart.toLocaleDateString(undefined, {
                                    weekday: 'long',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                })}`
                                : "Showing today's activity"}
                        </p>
                        {lastFetched && (
                            <p className="mt-1 text-xs text-slate-400">
                                Updated {lastFetched.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={loading}
                            className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-600 transition hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                    </div>
                </div>

                {error && (
                    <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                        {error}
                    </div>
                )}

                <section className="px-6 py-5">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Students logged in today
                        </h3>
                        {students.length > 0 && (
                            <span className="text-xs font-medium text-slate-400">
                                {students.length} {students.length === 1 ? 'student' : 'students'}
                            </span>
                        )}
                    </div>

                    {students.length === 0 ? (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                            {loading ? 'Fetching latest activity...' : 'No students have logged in yet today.'}
                        </div>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {students.map((student) => (
                                <div
                                    key={student.id}
                                    className="rounded-2xl border border-slate-200 px-4 py-4 shadow-sm transition hover:border-purple-200"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="text-base font-semibold text-slate-900">{student.name}</div>
                                            <div className="text-xs text-slate-500">{student.email}</div>
                                        </div>
                                        <div className="text-right text-xs text-slate-500">
                                            <div className="font-medium text-slate-700">Last login</div>
                                            <div>{formatLastLogin(student.lastLoginAt)}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                                        <span className="font-medium text-slate-700">
                                            Permit Practice: {formatDuration(student.totalMinutes)}
                                        </span>
                                        <span className="text-xs uppercase tracking-wide text-slate-400">
                                            {student.sessionIds.length} {student.sessionIds.length === 1 ? 'session' : 'sessions'}
                                        </span>
                                    </div>
                                    {student.sections.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {student.sections.map((section) => (
                                                <span
                                                    key={`${student.id}-${section.type}`}
                                                    className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600"
                                                >
                                                    {formatSectionLabel(section.type)} · {formatDuration(section.minutes)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {sessions.length > 0 && (
                    <section className="border-t border-slate-100 px-6 py-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Scheduled sessions
                        </h3>

                        <div className="mt-4 space-y-3">
                            {sessions.map((session) => (
                                <div
                                    key={session.id}
                                    className="rounded-2xl border border-slate-200 px-4 py-4 shadow-sm transition hover:border-purple-200"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="text-base font-semibold text-slate-900">{session.studentName}</div>
                                            <div className="text-xs text-slate-500">{session.studentEmail}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-600">
                                                {formatSectionLabel(session.section)}
                                            </span>
                                            <span className="text-sm font-medium text-slate-600">
                                                {formatDuration(session.durationMinutes)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                                        <span className="font-medium text-slate-700">
                                            {formatTimeRange(session.start, session.end)}
                                        </span>
                                        <span className="text-xs uppercase tracking-wide text-slate-400">
                                            {formatStatus(session.status)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {studentsWithTrackedTime.length > 0 && (
                    <section className="border-t border-slate-100 px-6 py-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Time spent by student
                            </h3>
                            {loading && (
                                <span className="text-xs font-medium text-slate-400">Refreshing data...</span>
                            )}
                        </div>

                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 text-sm">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-slate-600">Student</th>
                                        {sectionTypes.map((type) => (
                                            <th key={type} className="px-4 py-3 text-left font-semibold text-slate-600">
                                                {formatSectionLabel(type)}
                                            </th>
                                        ))}
                                        <th className="px-4 py-3 text-left font-semibold text-slate-600">Total time</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {studentsWithTrackedTime.map((student) => (
                                        <tr key={student.id} className="hover:bg-slate-50">
                                            <td className="whitespace-nowrap px-4 py-3">
                                                <div className="font-medium text-slate-900">{student.name}</div>
                                                <div className="text-xs text-slate-500">{student.email}</div>
                                            </td>
                                            {sectionTypes.map((type) => {
                                                const minutes = getMinutesForSection(student, type);
                                                return (
                                                    <td key={type} className="whitespace-nowrap px-4 py-3 text-slate-600">
                                                        {minutes > 0 ? formatDuration(minutes) : '—'}
                                                    </td>
                                                );
                                            })}
                                            <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-700">
                                                {formatDuration(student.totalMinutes)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
